/**
 * Brand colors of the WhatsApp guided webview, stored in the agent's
 * `settings` jsonb under `webview_primary_color` / `webview_secondary_color`.
 *
 * The API accepts `#rrggbb` or null (null deletes the key) and merges
 * `settings` key by key. A missing `settings` object, a missing key and a
 * null value all mean "default": the webview falls back to its CSS colors,
 * which are the defaults below.
 */

export const AGENT_WEBVIEW_COLOR_DEFAULTS = Object.freeze({
  primary: "#c4271b",
  secondary: "#ffc72c",
});

const SETTINGS_KEYS = {
  primary: "webview_primary_color",
  secondary: "webview_secondary_color",
};

export const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

/**
 * Lowercase a six-digit hex color, or return the fallback for anything the
 * API would reject (names, shorthand, missing `#`, empty, non-strings).
 * @param {unknown} value
 * @param {string|null} fallback
 * @returns {string|null}
 */
export const normalizeHexColor = (value, fallback) => {
  const text = String(value ?? "").trim();
  return HEX_COLOR_PATTERN.test(text) ? text.toLowerCase() : fallback;
};

/**
 * Effective colors of an agent, defaults applied.
 * @param {Object|null|undefined} agent
 * @returns {{ primary: string, secondary: string }}
 */
export const getAgentWebviewColors = (agent) => ({
  primary: normalizeHexColor(
    agent?.settings?.[SETTINGS_KEYS.primary],
    AGENT_WEBVIEW_COLOR_DEFAULTS.primary,
  ),
  secondary: normalizeHexColor(
    agent?.settings?.[SETTINGS_KEYS.secondary],
    AGENT_WEBVIEW_COLOR_DEFAULTS.secondary,
  ),
});

/**
 * `settings` patch for PUT: only the colors that changed, as null when they
 * went back to their default so the API drops the key.
 * @param {{ primary: string, secondary: string }} edited
 * @param {{ primary: string, secondary: string }} original
 * @returns {Object<string, string|null>}
 */
export const buildWebviewColorsPatch = (edited, original) =>
  Object.fromEntries(
    Object.entries(SETTINGS_KEYS)
      .filter(([field]) => edited[field] !== original[field])
      .map(([field, key]) => [
        key,
        edited[field] === AGENT_WEBVIEW_COLOR_DEFAULTS[field]
          ? null
          : edited[field],
      ]),
  );

/**
 * `settings` keys for POST: only the colors that differ from the defaults.
 * @param {{ primary: string, secondary: string }} colors
 * @returns {Object<string, string>}
 */
export const buildWebviewColorsForCreate = (colors) =>
  buildWebviewColorsPatch(colors, AGENT_WEBVIEW_COLOR_DEFAULTS);
